/**
 * SDK Embedded Styles - Tailwind CSS for Shadow DOM
 *
 * This file contains all the necessary Tailwind CSS styles for the widget
 * to render correctly inside a Shadow DOM, preventing style conflicts with
 * the host page.
 */

export const widgetStyles = `
/* Verivibe Widget Styles - Tailwind-based */

/* Reset and base styles */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* Color Palette - Brand & Slate theme */
:host {
  --brand-50: #eff6ff;
  --brand-100: #dbeafe;
  --brand-200: #bfdbfe;
  --brand-300: #93c5fd;
  --brand-400: #60a5fa;
  --brand-500: #0070FF;
  --brand-600: #0055CC;
  --brand-700: #0047b3;
  --brand-800: #003d99;

  --slate-50: #f8fafc;
  --slate-100: #f1f5f9;
  --slate-200: #e2e8f0;
  --slate-300: #cbd5e1;
  --slate-400: #94a3b8;
  --slate-500: #64748b;
  --slate-600: #475569;
  --slate-700: #334155;
  --slate-800: #1e293b;
  --slate-900: #0f172a;

  --amber-500: #f59e0b;
  --amber-600: #d97706;

  --red-50: #fef2f2;
  --red-500: #ef4444;

  --green-500: #10b981;
  --lime-500: #84cc16;
  --yellow-500: #eab308;
  --orange-500: #f97316;
  --pink-500: #ec4899;
}

/* Font families */
.font-sans { font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; }
.font-serif { font-family: ui-serif, Georgia, Cambria, "Times New Roman", Times, serif; }
.font-mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; }

/* Typography */
.text-xs { font-size: 0.75rem; line-height: 1rem; }
.text-sm { font-size: 0.875rem; line-height: 1.25rem; }
.text-base { font-size: 1rem; line-height: 1.5rem; }
.text-lg { font-size: 1.125rem; line-height: 1.75rem; }

.font-medium { font-weight: 500; }
.font-semibold { font-weight: 600; }
.font-bold { font-weight: 700; }

.uppercase { text-transform: uppercase; }
.no-underline { text-decoration: none; }

/* Colors */
.text-white { color: #ffffff; }
.text-slate-400 { color: var(--slate-400); }
.text-slate-500 { color: var(--slate-500); }
.text-slate-600 { color: var(--slate-600); }
.text-slate-700 { color: var(--slate-700); }
.text-slate-800 { color: var(--slate-800); }
.text-slate-900 { color: var(--slate-900); }
.text-red-500 { color: var(--red-500); }
.text-orange-500 { color: var(--orange-500); }
.text-yellow-500 { color: var(--yellow-500); }
.text-lime-500 { color: var(--lime-500); }
.text-pink-500 { color: var(--pink-500); }

.bg-white { background-color: #ffffff; }
.bg-white\\/80 { background-color: rgba(255, 255, 255, 0.8); }
.bg-slate-50 { background-color: var(--slate-50); }
.bg-slate-100 { background-color: var(--slate-100); }
.bg-slate-200 { background-color: var(--slate-200); }
.bg-red-50 { background-color: var(--red-50); }

.border-slate-100 { border-color: var(--slate-100); }
.border-slate-200 { border-color: var(--slate-200); }
.border-brand-100 { border-color: var(--brand-100); }

/* Layout */
.flex { display: flex; }
.items-center { align-items: center; }
.justify-center { justify-content: center; }
.justify-between { justify-content: space-between; }
.flex-col { flex-direction: column; }

.block { display: block; }
.w-full { width: 100%; }
.max-w-sm { max-width: 24rem; }
.max-w-md { max-width: 28rem; }
.max-w-fit { max-width: fit-content; }
.mx-auto { margin-left: auto; margin-right: auto; }

/* Spacing */
.gap-1 { gap: 0.25rem; }
.gap-1\\.5 { gap: 0.375rem; }
.gap-2 { gap: 0.5rem; }
.gap-3 { gap: 0.75rem; }
.gap-4 { gap: 1rem; }

.p-1 { padding: 0.25rem; }
.p-1\\.5 { padding: 0.375rem; }
.p-2 { padding: 0.5rem; }
.p-2\\.5 { padding: 0.625rem; }
.p-3 { padding: 0.75rem; }
.p-4 { padding: 1rem; }
.p-5 { padding: 1.25rem; }
.p-6 { padding: 1.5rem; }
.p-8 { padding: 2rem; }

.px-3 { padding-left: 0.75rem; padding-right: 0.75rem; }
.py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
.py-2\\.5 { padding-top: 0.625rem; padding-bottom: 0.625rem; }
.py-3 { padding-top: 0.75rem; padding-bottom: 0.75rem; }
.py-4 { padding-top: 1rem; padding-bottom: 1rem; }

.pl-2 { padding-left: 0.5rem; }
.pl-4 { padding-left: 1rem; }
.pr-4 { padding-right: 1rem; }

.mb-1 { margin-bottom: 0.25rem; }
.mb-1.5 { margin-bottom: 0.375rem; }
.mb-2 { margin-bottom: 0.5rem; }
.mb-4 { margin-bottom: 1rem; }
.mr-4 { margin-right: 1rem; }
.mt-4 { margin-top: 1rem; }

/* Borders */
.border { border-width: 1px; }
.border-2 { border-width: 2px; }
.border-dashed { border-style: dashed; }

.rounded-lg { border-radius: 0.5rem; }
.rounded-xl { border-radius: 0.75rem; }
.rounded-2xl { border-radius: 1rem; }
.rounded-full { border-radius: 9999px; }

/* Shadows */
.shadow-sm { box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05); }
.shadow-md { box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1); }
.shadow-lg { box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1); }
.shadow-xl { box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1); }
.shadow-2xl { box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.25); }

/* Brand Shadows */
.shadow-brand-sm { box-shadow: 0 1px 2px 0 rgba(0, 112, 255, 0.05); }
.shadow-brand { box-shadow: 0 4px 6px -1px rgba(0, 112, 255, 0.1), 0 2px 4px -2px rgba(0, 112, 255, 0.1); }
.shadow-brand-md { box-shadow: 0 4px 6px -1px rgba(0, 112, 255, 0.15), 0 2px 4px -2px rgba(0, 112, 255, 0.1); }
.shadow-brand-lg { box-shadow: 0 10px 15px -3px rgba(0, 112, 255, 0.15), 0 4px 6px -4px rgba(0, 112, 255, 0.1); }
.shadow-brand-xl { box-shadow: 0 20px 25px -5px rgba(0, 112, 255, 0.15), 0 8px 10px -6px rgba(0, 112, 255, 0.1); }

/* Sizes */
.h-7 { height: 1.75rem; }
.w-7 { width: 1.75rem; }
.h-8 { height: 2rem; }
.w-8 { width: 2rem; }
.h-10 { height: 2.5rem; }
.w-10 { width: 2.5rem; }
.h-12 { height: 3rem; }
.w-12 { width: 3rem; }
.h-14 { height: 3.5rem; }
.w-14 { width: 3.5rem; }

/* Opacity */
.opacity-50 { opacity: 0.5; }
.opacity-60 { opacity: 0.6; }

/* Transitions */
.transition-all { transition-property: all; transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1); transition-duration: 150ms; }
.transition-colors { transition-property: color, background-color, border-color; transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1); transition-duration: 150ms; }
.transition-opacity { transition-property: opacity; transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1); transition-duration: 150ms; }
.transition-transform { transition-property: transform; transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1); transition-duration: 150ms; }
.transition-shadow { transition-property: box-shadow; transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1); transition-duration: 150ms; }

.duration-300 { transition-duration: 300ms; }

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideInFromBottom {
  from { transform: translateY(10px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

@keyframes zoomIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.animate-in { animation: fadeIn 300ms ease-out; }
.fade-in { animation: fadeIn 300ms ease-out; }
.zoom-in { animation: zoomIn 300ms ease-out; }
.slide-in-from-bottom-2 { animation: slideInFromBottom 300ms ease-out; }
.slide-in-from-bottom-4 { animation: slideInFromBottom 300ms ease-out; }

/* Forms */
input, textarea, select {
  font-family: inherit;
  font-size: 100%;
  line-height: inherit;
  color: inherit;
  margin: 0;
}

input, textarea, select {
  outline: none;
}

input:focus, textarea:focus, select:focus {
  outline: 2px solid var(--brand-500);
  outline-offset: 2px;
}

/* Input styling with explicit colors for Shadow DOM */
input[type="email"],
input[type="text"],
textarea {
  color: var(--slate-900); /* Dark text for readability */
  background-color: #ffffff; /* White background */
}

input::placeholder,
textarea::placeholder {
  color: var(--slate-400); /* Light gray placeholder - clearly visible */
  opacity: 1; /* Override browser defaults */
}

.resize-none { resize: none; }

/* Buttons */
button {
  cursor: pointer;
  font-family: inherit;
  font-size: 100%;
  line-height: inherit;
  color: inherit;
  margin: 0;
  border: none;
  background: none;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* Hover states */
.hover\\:opacity-100:hover { opacity: 1; }
.hover\\:bg-slate-200:hover { background-color: var(--slate-200); }
.hover\\:bg-red-50:hover { background-color: var(--red-50); }
.hover\\:text-slate-600:hover { color: var(--slate-600); }
.hover\\:text-red-500:hover { color: var(--red-500); }
.hover\\:text-brand-600:hover { color: var(--brand-600); }
.hover\\:scale-110:hover { transform: scale(1.1); }
.hover\\:scale-125:hover { transform: scale(1.25); }
.hover\\:brightness-110:hover { filter: brightness(1.1); }
.hover\\:shadow-xl:hover { box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1); }
.hover\\:shadow-2xl:hover { box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.25); }
.hover\\:-translate-y-0.5:hover { transform: translateY(-0.125rem); }

/* Active states */
.active\\:scale-95:active { transform: scale(0.95); }

/* Group hover (parent hover affects child) */
.group:hover .group-hover\\:opacity-100 { opacity: 1; }
.group:hover .group-hover\\:text-brand-600 { color: var(--brand-600); }

/* Text alignment */
.text-center { text-align: center; }

/* Backdrop blur */
.backdrop-blur-md { backdrop-filter: blur(12px); }
.bg-white\\/95 { background-color: rgba(255, 255, 255, 0.95); }

/* Focus styles */
.focus\\:outline-none:focus { outline: none; }
.focus\\:ring-2:focus { box-shadow: 0 0 0 2px var(--brand-500); }
.focus\\:ring-brand-500:focus { box-shadow: 0 0 0 2px var(--brand-500); }
.focus\\:border-transparent:focus { border-color: transparent; }

/* Disabled states */
.disabled\\:opacity-50:disabled { opacity: 0.5; }

/* Cursor */
.cursor-pointer { cursor: pointer; }

/* Space between */
.space-y-3 > * + * { margin-top: 0.75rem; }
.space-y-4 > * + * { margin-top: 1rem; }

/* Object fit */
.object-cover { object-fit: cover; }

/* Additional responsive styles for mobile */
@media (min-width: 640px) {
  .sm\\:p-4 { padding: 1rem; }
  .sm\\:p-5 { padding: 1.25rem; }
  .sm\\:p-6 { padding: 1.5rem; }
  .sm\\:p-8 { padding: 2rem; }
  .sm\\:py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
  .sm\\:py-2\\.5 { padding-top: 0.625rem; padding-bottom: 0.625rem; }
  .sm\\:py-4 { padding-top: 1rem; padding-bottom: 1rem; }
  .sm\\:text-sm { font-size: 0.875rem; line-height: 1.25rem; }
  .sm\\:text-base { font-size: 1rem; line-height: 1.5rem; }
  .sm\\:text-lg { font-size: 1.125rem; line-height: 1.75rem; }
  .sm\\:max-w-sm { max-width: 24rem; }
  .sm\\:max-w-md { max-width: 28rem; }
  .sm\\:rounded-xl { border-radius: 0.75rem; }
  .sm\\:rounded-2xl { border-radius: 1rem; }
  .sm\\:mb-4 { margin-bottom: 1rem; }
  .sm\\:gap-4 { gap: 1rem; }
  .sm\\:w-6 { width: 1.5rem; }
  .sm\\:h-6 { height: 1.5rem; }
  .sm\\:w-8 { width: 2rem; }
  .sm\\:h-8 { height: 2rem; }
  .sm\\:w-10 { width: 2.5rem; }
  .sm\\:h-10 { height: 2.5rem; }
  .sm\\:w-14 { width: 3.5rem; }
  .sm\\:h-14 { height: 3.5rem; }
}

/* Pointer events */
.pointer-events-none { pointer-events: none; }
.pointer-events-auto { pointer-events: auto; }
`;
